import { Field, useFormikContext } from 'formik';
import React, {FC, useState} from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import ActivityFrame from '../activity-frame/activity-frame';
import Label from '../label/label';
import { requiredValidation } from '../../utilities/Validation';
import { ExecuteCommand } from '../../models/ExecuteCommand';
import Commands from '../../constants/commands';
import { AccountSubCmds } from '../../constants/sub-commands';
import { executeCommand } from '../../services/executeCommandService';
import { COMMAND_STATE } from '../../constants/command-status';
import { useDialog } from "../dialog/DialogContext";
import { getErrorMessage } from '../../services/utilities/ServiceError';
import {toast} from "react-toastify";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEye, faEyeSlash} from "@fortawesome/free-solid-svg-icons";

interface AuthAccountDetailsProps {
    isAccountPopulated: boolean,
    currentItem: any,
    isAuthTenetRequired: boolean,
    isLicensed: boolean
}

export const AuthAccountDetails: FC<AuthAccountDetailsProps> = ({ isAccountPopulated, currentItem , isAuthTenetRequired, isLicensed}) => {
    const { translate } = useLocale();
    const dialog = useDialog();
    const { setFieldValue, values, errors, dirty } = useFormikContext<any>();
    const [busy, setBusy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    /**
     * The account is queried using saved account details only. Therefore, to query the account:
     * 1. The account details must be populated.
     * 2. There must be no unsaved changes.
     * @returns
     */
    const canTestAccount = () => {
        return isLicensed && isAccountPopulated && !dirty;
    }

    const onAnonymousConnectionChanged = (event) => {
        const isChecked = event.target.checked;
        setFieldValue("anonymousConnection", isChecked)
    }

    const errorHandler = (accessor) => {
        const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }
            , errors as any);
        if (error) {
            return <span className='text-red-600 text-xs'>{error}</span>
        } else {
            return null;
        }
    };

    const testConnection = () => {
        if (dirty) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unsavedChanges), message: translate.byKey(TranslationKeys.saveChangesBeforeTesting) });
            return;
        }

        setBusy(true);
        const command: ExecuteCommand = {
            cmd: Commands.Account.label,
            cmdSubType: AccountSubCmds.Test_Account,
            entityId: currentItem.id,
            jsonResponse: ""
        }

        executeCommand(command).then((response: any) => {
            if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {

                if (values.enabled === false) {
                    toast?.success(translate.byKey(TranslationKeys.successConnectedToDisabledAccount));
                }
                else {
                    toast?.success(translate.byKey(TranslationKeys.successConnectedToAccount));
                }
            }
            else {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.testAccountFailed) });
            }
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    const onValidateSecret = (value: String) => {
        // Ignores validation only if secret is already captured and no new edits are made
        if (currentItem?.isPasswordCaptured && !value) {
            return null;
        }

        return requiredValidation(value, translate);
    }

    return <ActivityFrame busy={busy} className='flex flex-col space-y-4 flex-grow bg-white'>
        {/* Account */}
        <div>
            <div className="mb-3 text-portal-blue-primary-shade2 text-md font-semibold">
                {translate.byKey(TranslationKeys.account)}:
            </div>
            <div className="flex flex-row mt-2">
                {/* Issuer input field */}
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="issuerLabel">
                        <Label htmlFor='details.issuer.value' required
                               className="text-xs text-gray-600">{translate.byKey(TranslationKeys.issuer)}:</Label>
                        {errorHandler("details.issuer.value")}
                    </div>
                    <Field
                        id='issuerInputFieldId'
                        name="details.issuer.value"
                        type="text"
                        autoComplete="off"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
                {/* tenantId input field */}
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="tenantIdLabel">
                        <Label htmlFor='details.tenantId.value' required = {isAuthTenetRequired}
                               className="text-xs text-gray-600">{translate.byKey(TranslationKeys.tenantId)}:</Label>
                        {errorHandler("details.tenantId.value")}
                    </div>
                    <Field
                        name="details.tenantId.value"
                        type="text"
                        autoComplete="off"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => {
                            if (isAuthTenetRequired) {
                                return requiredValidation(value, translate);
                            }
                            return undefined;
                        }}/>
                </div>
            </div>

            <div className="flex flex-row mt-2">
                {/* clientId input field */}
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="clientId">
                        <Label htmlFor='details.clientId.value' required
                               className="text-xs text-gray-600">{translate.byKey(TranslationKeys.clientId)}:</Label>
                        {errorHandler("details.clientId.value")}
                    </div>
                    <Field
                        name="details.clientId.value"
                        type="text"
                        autoComplete="off"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
                {/* clientSecret input field */}
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="password">
                        <Label htmlFor='tempPassword' required
                               className="text-xs text-gray-600">{translate.byKey(TranslationKeys.clientSecret)}:</Label>
                        {errorHandler("tempPassword")}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type={showPassword ? 'text' : 'password'}
                            name="tempPassword"
                            autoComplete="new-password"
                            className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={(value) => onValidateSecret(value)}
                            placeholder={(currentItem?.tempPassword) ? "********" : translate.byKey(TranslationKeys.enterNewClientSecret)} />
                        <div className="flex relative w-6 ml-1">
                            <FontAwesomeIcon
                                className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                onClick={() => setShowPassword(showPassword => !showPassword)}
                                icon={showPassword ? faEye : faEyeSlash} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Anonymous Connection Checkbox */}
            {/* Hidden for now */}
            <div className="hidden flex-row mt-4">
                <Field
                    id={`checkbox-anonymousConnection`}
                    type="checkbox"
                    name="details.anonymousConnection.value"
                    checkbox={values.adminConsent}
                    onChange={onAnonymousConnectionChanged} />
                <Label htmlFor={`checkbox-anonymousConnection`} className="ml-2">{translate.byKey(TranslationKeys.adminConsent)}</Label>
            </div>
        </div>
        {/* Primo Items */}
        <div>
            <div className="hidden mb-3 text-portal-blue-primary-shade2 text-md font-semibold">
                {translate.byKey(TranslationKeys.connection)}:
            </div>
            <div className="flex flex-row mt-2">
                {/* Redirect field */}
                <div className="hidden flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="hostLabel">
                        <Label htmlFor='details.redirectUri.value' required
                               className="text-xs text-gray-600">{translate.byKey(TranslationKeys.host)}:</Label>
                        {errorHandler("details.redirectUri.value")}
                    </div>
                    <Field
                        name="details.redirectUri.value"
                        type="text"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
                {/*Empty*/}
                <div className="flex flex-col w-1/6">
                </div>
            </div>
        </div>
        <div className='pb-4'>
            <button
                type="button"
                className={canTestAccount()
                    ? " h-8 px-2 mt-1 secondary bg-sidemenu-focus text-white rounded-lg shadow-lg"
                    : " secondary secondary-disabled cursor-not-allowed rounded-lg shadow-lg h-8 px-2 mt-1"}
                disabled={!canTestAccount()}
                onClick={testConnection}>
                {translate.byKey(TranslationKeys.testAccount)}
            </button>
        </div>
    </ActivityFrame>
};
